import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'react-use'
import { getV3ColorCssVariable } from '../../../design-system/colors_v3'
import { computeAgeLabel, getDisplayedName } from '../../../misc/patient.utilities'
import { LightPatient, Sex } from '../../../model/Patient'
import { IdentityCard, DropdownItem, Icon, SearchInput } from '../../shared'
import { PatientDropDownItemSelectorOwnProps } from './PatientDropDownItemSelector.model'
import { useGetInfinitePatient } from '../../../hooks/queries/patients'
import { isEqual } from 'lodash'
import styles from './PatientDropDownItemSelector.module.scss'

const PATIENT_FILTERS_INITIAL_STATE = { search: '' }

export const PatientDropDownItemSelector: FunctionComponent<
  PatientDropDownItemSelectorOwnProps
> = ({ selectedPatient, setSelectedPatient, hasInseeNumber = false, disabled = false }) => {
  const [patientSearchText, setPatientSearchText] = useState('')
  const [patientFilters, setPatientFilters] = useState(PATIENT_FILTERS_INITIAL_STATE)
  const [patients, setPatients] = useState<LightPatient[]>([])
  const {
    query: { isLoading },
    patientList,
  } = useGetInfinitePatient({
    enabled: !isEqual(patientFilters, PATIENT_FILTERS_INITIAL_STATE),
    filters: { ...patientFilters, hasInseeNumber },
  })

  useEffect(() => {
    if (!isLoading) {
      setPatients(patientList)
    }
  }, [patientList, isLoading])

  const renderPatientOption = (patient: LightPatient, isHovered: boolean) => {
    const { firstname, lastname } = getDisplayedName(patient)
    return (
      <DropdownItem selected={isHovered}>
        <div className={styles.patientDropDownItemSelectorContainer}>
          {patient.sex !== Sex.UNKNOWN && (
            <Icon
              icon={patient.sex === Sex.FEMALE ? 'female' : 'male'}
              size="micro"
              color={getV3ColorCssVariable('shades', 'shade2')}
            />
          )}
          <div key={patient.id} className={styles.patientDropDownItemNamesWithAgeContainer}>
            <span className={styles.patientDropDownItemNames}>
              {firstname} <span className={styles.patientDropDownItemLastname}>{lastname}</span>
            </span>
            <span className={styles.patientDropDownItemAge}>
              {computeAgeLabel(patient.birthDate)}
            </span>
          </div>
        </div>
      </DropdownItem>
    )
  }

  useDebounce(
    () => {
      setPatientFilters((prev) => {
        return patientSearchText.length >= 3
          ? { ...prev, search: patientSearchText }
          : { ...prev, search: PATIENT_FILTERS_INITIAL_STATE.search }
      })
    },
    500,
    [patientSearchText],
  )

  const handleSelectPatient = useCallback(
    (selected: LightPatient) => {
      setSelectedPatient(selected)
      setPatientSearchText('')
    },
    [setSelectedPatient, setPatientSearchText],
  )

  const displayedName = selectedPatient ? getDisplayedName(selectedPatient) : null

  return (
    <>
      <SearchInput
        placeholder="Rechercher un patient"
        value={patientSearchText}
        onChange={({ target: { value } }) => setPatientSearchText(value)}
        results={patients}
        renderResult={renderPatientOption}
        onSelect={handleSelectPatient}
        disabled={disabled}
        testId="input-patient-search"
      />
      {selectedPatient && (
        <div className={styles.identityCartContainer}>
          <IdentityCard
            title={
              displayedName ? `${displayedName.firstname} ${displayedName.lastname}` : 'Inconnu'
            }
            icon={selectedPatient.sex === Sex.FEMALE ? 'female' : 'male'}
            subtitle={computeAgeLabel(selectedPatient.birthDate)}
            onRemove={() => setSelectedPatient(undefined)}
            disabled={disabled}
          />
        </div>
      )}
    </>
  )
}
